import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Trash2, AlertTriangle, Users, Building } from "lucide-react";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";

interface DepartmentInfo {
  id: string;
  name: string;
  description: string;
  userCount?: number;
  childrenCount?: number;
  children?: Array<{ id: string; name: string }>;
  users?: Array<any>;
}

export default function DeleteDepartmentPage() {
  const navigate = useNavigate();
  const { departmentId } = useParams<{ departmentId: string }>();
  const hasDeletePermission = checkPermission("department_delete");

  useEffect(() => {
    if (!hasDeletePermission) {
      navigate("/employees");
    }
  }, [hasDeletePermission, navigate]);

  const [department, setDepartment] = useState<DepartmentInfo | null>(null);
  const [userCount, setUserCount] = useState<number>(0);
  const [childrenCount, setChildrenCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-red-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-red-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700";

  useEffect(() => {
    const load = async () => {
      if (!departmentId) return;
      try {
        setLoading(true);
        setError("");

        const [departmentData, usersData, departmentsData] = await Promise.all([
          ApiService.getDepartment(departmentId),
          ApiService.getUsers().catch(() => ({ data: [] })),
          ApiService.getDepartments().catch(() => ({ data: [] })),
        ]);

        setDepartment(departmentData);

        const users = usersData.data || [];
        const departmentUsers = users.filter((user: any) => user.departmentId === departmentId);
        setUserCount(departmentUsers.length);

        const allDepartments = departmentsData.data || [];
        const children = allDepartments.filter((dept: any) => dept.parentId === departmentId);
        setChildrenCount(children.length);
      } catch (err: any) {
        setError(err.message || "Veriler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [departmentId]);

  const handleDelete = async () => {
    if (!departmentId || !department) return;
    if (confirmText !== department.name) {
      setError("Departman adını doğru yazmadınız!");
      return;
    }
    try {
      setDeleting(true);
      setError("");
      await ApiService.deleteDepartment(departmentId);
      navigate("/employees?tab=departments");
    } catch (err: any) {
      setError(err.message || "Departman silinemedi");
    } finally {
      setDeleting(false);
    }
  };

  if (!hasDeletePermission) {
    return null;
  }

  const hasUsers = userCount > 0;
  const hasChildren = childrenCount > 0;

  return (
    <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Üyeler</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button onClick={() => navigate("/employees?tab=users")} className={`pb-3 transition-all duration-200 text-gray-500 hover:text-gray-900`}>
                Kullanıcılar
              </button>
              <button
                onClick={() => navigate("/employees?tab=departments")}
                className={`pb-3 transition-all duration-200 text-gray-900 font-semibold border-b-2 border-gray-900`}>
                Departmanlar
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5 sm:p-6">
        <div className="max-w-6xl mx-auto">

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg mb-4">{error}</div>}

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
            </div>
          ) : department ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
              <div className="space-y-5">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Departmanı Sil</h1>
                    <p className="text-sm text-gray-600 mt-1">Bu işlem geri alınamaz. Lütfen silme onayını dikkatli bir şekilde tamamlayın.</p>
                    <div className="mt-3 h-1 w-20 rounded-full bg-red-200" />
                  </div>
                  <button
                    onClick={() => navigate(-1)}
                    disabled={deleting}
                    className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50">
                    Geri Dön
                  </button>
                </div>

                <div className={sectionClassName}>
                  <div className="mb-3">
                    <span className={`${sectionBadgeClassName} gap-1.5`}>
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Dikkat
                    </span>
                  </div>
                  <p className="text-red-700 text-sm mb-3">
                    Bu işlem geri alınamaz. Departmanı sildikten sonra aşağıdaki veriler kalıcı olarak kaybolacaktır:
                  </p>
                  <ul className="space-y-2 text-red-700 text-sm">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      Departman bilgileri ve ayarları
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      Departman izinleri
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      İlişkili tüm veriler
                    </li>
                  </ul>
                </div>

                <div className={sectionClassName}>
                  <div className="mb-3">
                    <span className={`${sectionBadgeClassName} gap-1.5`}>
                      <Building className="w-3.5 h-3.5" />
                      Departman Bilgileri
                    </span>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-500 text-xs mb-1">Departman Adı</label>
                        <p className="text-gray-900 font-medium">{department.name}</p>
                      </div>
                      {department.description && (
                        <div>
                          <label className="block text-gray-500 text-xs mb-1">Açıklama</label>
                          <p className="text-gray-700 text-sm">{department.description}</p>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="w-4 h-4 text-red-600" />
                          <span className="text-red-700 text-sm font-medium">Kullanıcı Sayısı</span>
                        </div>
                        <p className={`text-2xl font-bold ${hasUsers ? "text-red-600" : "text-red-400"}`}>{userCount}</p>
                        {hasUsers && <p className="text-red-600 text-xs mt-1">Bu kullanıcılar etkilenecek</p>}
                      </div>
                      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Building className="w-4 h-4 text-red-600" />
                          <span className="text-red-700 text-sm font-medium">Alt Departman</span>
                        </div>
                        <p className={`text-2xl font-bold ${hasChildren ? "text-red-600" : "text-red-400"}`}>{childrenCount}</p>
                        {hasChildren && <p className="text-red-600 text-xs mt-1">Bu departmanlar da silinecek</p>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={sectionClassName}>
                  <div className="mb-3">
                    <span className={`${sectionBadgeClassName} gap-1.5`}>
                      <Trash2 className="w-3.5 h-3.5" />
                      Silme Onayı
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">Silme işlemini onaylamak için departman adını tam olarak yazın:</p>
                  <div className="mb-4">
                    <p className="text-red-700 font-mono text-sm mb-2 bg-red-50 p-2 rounded border border-red-200">{department.name}</p>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="Departman adını buraya yazın..."
                      className={`${fieldClassName} ${
                        confirmText ? (confirmText === department.name ? "border-green-400 bg-green-50" : "border-red-400 bg-red-50") : ""
                      }`}
                    />
                  </div>
                  {confirmText && confirmText !== department.name && (
                    <p className="text-red-600 text-xs bg-red-50 p-2 rounded border border-red-100">Girilen metin departman adı ile eşleşmiyor.</p>
                  )}
                  {confirmText === department.name && (
                    <p className="text-green-600 text-xs bg-green-50 p-2 rounded border border-green-100">Departman adı doğru girildi.</p>
                  )}
                </div>

                <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => navigate(-1)}
                      disabled={deleting}
                      className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm disabled:opacity-50">
                      İptal
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={confirmText !== department.name || deleting || loading}
                      className="px-4 py-2 bg-gradient-to-r from-red-600 to-rose-700 text-white rounded-md hover:from-red-700 hover:to-rose-800 disabled:opacity-60 text-sm flex items-center justify-center gap-2">
                      {deleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Siliniyor...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Departmanı Sil
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="text-center py-16 text-gray-500">
                <Building className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">Departman bilgileri yüklenemedi</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
